<template>
  <div class="mb-4">
    <label :for="inputId" class="block text-sm font-medium text-gray-700 mb-1">{{ label }}</label>
    
    <!-- Länderauswahl -->
    <div class="mb-2">
      <label class="block text-xs font-medium text-gray-600 mb-1">Land</label>
      <div class="flex space-x-2">
        <button
          @click="selectedCountry = 'germany'"
          :class="[
            'flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
            selectedCountry === 'germany'
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          ]"
        >
          <span class="text-lg">🇩🇪</span>
          <span>Deutschland</span>
        </button>
        <button
          @click="selectedCountry = 'europe'"
          :class="[
            'flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-colors',
            selectedCountry === 'europe'
              ? 'bg-blue-50 border-blue-300 text-blue-700'
              : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
          ]"
        >
          <span class="text-lg">🇪🇺</span>
          <span>Europa</span>
        </button>
      </div>
    </div>
    
    <!-- Kennzeichen-Input -->
    <div class="flex">
      <input 
        :id="inputId"
        type="text" 
        :value="modelValue"
        @input="handleInput"
        :class="[
          'w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4',
          disabled ? 'bg-gray-50 text-gray-700 border-gray-200' : 'bg-white'
        ]"
        :placeholder="placeholder"
        :required="required"
        :disabled="disabled"
        maxlength="12"
      >
      <slot name="button"></slot>
    </div>
    
    <!-- Format-Hinweis -->
    <p class="mt-1 text-xs text-gray-500">
      <span v-if="selectedCountry === 'germany'">
        Format: Ort-BuchstabenZahlen (z.B. B-JJ999, MS-WU180, B-JJ999E für E-Autos, M-AB123H für Oldtimer)
      </span>
      <span v-else>
        Format: Buchstaben und Zahlen ohne Bindestriche (z.B. ABC123, XY789)
      </span>
    </p>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { normalizePlateInput } from '../utils/licensePlate'

interface Props {
  modelValue: string
  label?: string
  inputId?: string
  placeholder?: string
  required?: boolean
  disabled?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: string): void
}

const props = withDefaults(defineProps<Props>(), {
  label: 'Kennzeichen',
  inputId: 'licensePlate',
  placeholder: 'z.B. PS-XY1234 oder PS-XY1234E',
  required: false,
  disabled: false
})

const emit = defineEmits<Emits>()

const selectedCountry = ref<'germany' | 'europe'>('germany')

// Automatische Großschreibung und Formatierung
const handleInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  // Umlaute transliterieren (RÜD → RUED) — kanonische Form, siehe utils/licensePlate.ts
  let value = normalizePlateInput(target.value)

  if (selectedCountry.value === 'germany') {
    // Deutsches Format: Entferne alle Leerzeichen und füge Bindestriche hinzu
    value = value.replace(/\s/g, '')
    // Füge Bindestriche nach dem Ortskürzel hinzu (1-4 Buchstaben; 4 = transliterierter Umlaut, z.B. RUED)
    // Unterstützt E-Kennzeichen mit optionalem E am Ende
    value = value.replace(/^([A-Z]{1,4})([A-Z]{1,2})(\d{1,4}[A-Z]?)$/, '$1-$2$3')
    // Fallback für andere Formate (mit optionalem E)
    value = value.replace(/^([A-Z]{1,4})(\d{1,4}[A-Z]?)$/, '$1-$2')
  } else {
    // Europäisches Format: Nur Buchstaben und Zahlen, keine Bindestriche
    value = value.replace(/[^A-Z0-9]/g, '')
  }
  
  emit('update:modelValue', value)
}

// Validierung basierend auf Land
const isValidFormat = computed(() => {
  const plate = props.modelValue.trim()
  if (!plate) return false
  
  if (selectedCountry.value === 'germany') {
    // Deutsche Kennzeichen-Formate (inkl. E-Kennzeichen)
    // Ortskürzel {1,4}: 4 Buchstaben = transliterierter Umlaut-Kreis (RÜD → RUED, DEV-215)
    const patterns = [
      /^[A-Z]{1,4}[- ]?[A-Z]{1,2}[- ]?\d{1,4}[A-Z]?$/, // Standard (mit optionalem E)
      /^[A-Z]{1,4}[- ]?[A-Z][- ]?[A-Z][- ]?\d{1,4}[A-Z]?$/, // Behördenkennzeichen (mit optionalem E)
      /^[A-Z]{1,4}[- ]?[A-Z]{2}\d{1,4}[A-Z]?$/, // Behördenkennzeichen ohne Bindestrich (mit optionalem E)
      /^[A-Z]{1,4}[- ]?[A-Z]{2}[- ]?\d{1,4}[A-Z]?$/ // Normales Kennzeichen (mit optionalem E)
    ]
    return patterns.some(pattern => pattern.test(plate))
  } else {
    // Europäisches Format: Mindestens 3 Zeichen, nur Buchstaben und Zahlen
    return /^[A-Z0-9]{3,12}$/.test(plate)
  }
})

// Expose computed properties for parent components
defineExpose({
  isValidFormat,
  selectedCountry
})
</script> 